/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Client } from '../../shared/entities/client';
import { Action, ActionCreators } from './actions';

export interface State {
  readonly clients: Client[] | undefined;
  readonly isRequestingClients: boolean;
  readonly isClientsRequestFinished: boolean;
  readonly clientsRequestError?: string;

  readonly currentClient: Client | undefined;
  readonly isRequestingCurrentClient: boolean;
  readonly isCurrentClientRequestFinished: boolean;
  readonly currentClientRequestError?: string;

  readonly isProvisioningClient: boolean;
  readonly isClientProvisioningFinished: boolean;
  readonly clientProvisioningError?: string;

  readonly isDeletingClient: boolean;
  readonly isDeletingClientFinished: boolean;
  readonly deleteClientError?: string;

  readonly isUpdatingClient: boolean;
  readonly isUpdatingClientFinished: boolean;
  readonly updateClientError?: string;
}

export const initialClientProvisioningState: State = {
  clients: undefined,
  isRequestingClients: false,
  isClientsRequestFinished: false,
  clientsRequestError: undefined,

  currentClient: undefined,
  isRequestingCurrentClient: false,
  isCurrentClientRequestFinished: false,
  currentClientRequestError: undefined,

  isProvisioningClient: false,
  isClientProvisioningFinished: false,
  clientProvisioningError: undefined,

  isDeletingClient: false,
  isDeletingClientFinished: false,
  deleteClientError: undefined,

  isUpdatingClient: false,
  isUpdatingClientFinished: false,
  updateClientError: undefined,
};

export default function reducer(state: State = initialClientProvisioningState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    // For requesting list of clients
    case ActionCreators.RequestClients.type:
      partialState = {
        clients: undefined,
        isRequestingClients: true,
        isClientsRequestFinished: false,
        clientsRequestError: undefined,
      };
      break;

    case ActionCreators.ReceivedClientsSuccess.type: {
      partialState = {
        clients: action.payload.response,
        isRequestingClients: false,
        isClientsRequestFinished: true,
        clientsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedClientsError.type:
      partialState = {
        isRequestingClients: false,
        isClientsRequestFinished: true,
        clientsRequestError: action.payload.message,
      };
      break;

    case ActionCreators.ClearReceivedClientsError.type:
      partialState = {
        clientsRequestError: undefined,
      };
      break;

    // For requesting a specific client
    case ActionCreators.RequestCurrentClient.type:
      partialState = {
        currentClient: undefined,
        isRequestingCurrentClient: true,
        isCurrentClientRequestFinished: false,
        currentClientRequestError: undefined,
      };
      break;

    case ActionCreators.ReceivedCurrentClientSuccess.type: {
      partialState = {
        currentClient: action.payload.response,
        isRequestingCurrentClient: false,
        isCurrentClientRequestFinished: true,
        currentClientRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedCurrentClientError.type:
      partialState = {
        isRequestingCurrentClient: false,
        isCurrentClientRequestFinished: true,
        currentClientRequestError: action.payload.message,
      };
      break;

    case ActionCreators.ClearReceivedCurrentClientError.type:
      partialState = {
        currentClientRequestError: undefined,
      };
      break;

    case ActionCreators.ClearCurrentClient.type:
      partialState = {
        currentClient: undefined,
        isRequestingCurrentClient: false,
        isCurrentClientRequestFinished: false,
        currentClientRequestError: undefined,
      };
      break;

    // For creating a client
    case ActionCreators.ProvisionClient.type:
      partialState = {
        isProvisioningClient: true,
        isClientProvisioningFinished: false,
        clientProvisioningError: undefined,
      };
      break;

    case ActionCreators.ClientProvisioningSuccess.type:
      partialState = {
        isProvisioningClient: false,
        isClientProvisioningFinished: true,
        clientProvisioningError: undefined,
        currentClient: action.payload,
      };
      break;

    case ActionCreators.ClientProvisioningError.type:
      partialState = {
        isProvisioningClient: false,
        isClientProvisioningFinished: true,
        clientProvisioningError: action.payload.message,
      };
      break;

    case ActionCreators.ClearClientProvisioningError.type:
      partialState = {
        clientProvisioningError: undefined,
      };
      break;

    // For deleting a client
    case ActionCreators.DeleteClient.type:
      partialState = {
        isDeletingClient: true,
        isDeletingClientFinished: false,
        deleteClientError: undefined,
      };
      break;

    case ActionCreators.DeleteClientSuccess.type:
      partialState = {
        isDeletingClient: false,
        isDeletingClientFinished: true,
        deleteClientError: undefined,
      };
      break;

    case ActionCreators.DeleteClientError.type:
      partialState = {
        isDeletingClient: false,
        isDeletingClientFinished: true,
        deleteClientError: action.payload.message,
      };
      break;

    case ActionCreators.ClearDeleteClientError.type:
      partialState = {
        deleteClientError: undefined,
      };
      break;

    // For updating a client
    case ActionCreators.UpdateClient.type:
      partialState = {
        isUpdatingClient: true,
        isUpdatingClientFinished: false,
        updateClientError: undefined,
      };
      break;

    case ActionCreators.UpdateClientSuccess.type:
      partialState = {
        isUpdatingClient: false,
        isUpdatingClientFinished: true,
        updateClientError: undefined,
        currentClient: action.payload,
      };
      break;

    case ActionCreators.UpdateClientError.type:
      partialState = {
        isUpdatingClient: false,
        isUpdatingClientFinished: true,
        updateClientError: action.payload.message,
      };
      break;

    case ActionCreators.ClearUpdateClientError.type:
      partialState = {
        updateClientError: undefined,
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
